/**
 * widget-contacts-editor.tsx — Merchant tự thêm / xếp / xoá kênh liên hệ ở màn chào widget Live Chat
 * (Zalo, Messenger, Telegram, WhatsApp, điện thoại, email, trung tâm trợ giúp…).
 */
import { useTranslation } from 'react-i18next'
import { ArrowDown, ArrowUp, BookOpen, Globe, Mail, Phone, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  CONTACT_META, CONTACT_TYPES, MAX_CONTACTS, contactError, type ContactLink, type ContactType,
} from '@/lib/widget-contacts'

const FALLBACK_ICON = { phone: Phone, email: Mail, website: Globe, help: BookOpen } as const

export function ContactTypeIcon({ type, className = 'h-4 w-4' }: { type: ContactType; className?: string }) {
  const logo = CONTACT_META[type].logo
  if (logo) return <img src={logo} alt="" className={className} />
  const Icon = FALLBACK_ICON[type as keyof typeof FALLBACK_ICON] ?? Globe
  return <Icon className={`${className} text-muted-foreground`} />
}

export function ContactLinksEditor({
  value, onChange, showErrors,
}: {
  value: ContactLink[]
  onChange: (v: ContactLink[]) => void
  /** Hiện lỗi từng dòng (sau khi bấm Lưu) — tránh báo đỏ khi đang gõ dở. */
  showErrors: boolean
}) {
  const { t } = useTranslation('chanSocial')
  const patch = (i: number, p: Partial<ContactLink>) => onChange(value.map((c, j) => (j === i ? { ...c, ...p } : c)))
  const move = (i: number, d: -1 | 1) => {
    const next = [...value]
    ;[next[i], next[i + d]] = [next[i + d], next[i]]
    onChange(next)
  }
  // Gợi ý kênh chưa có trong danh sách để thêm nhanh.
  const nextType = CONTACT_TYPES.find((ct) => !value.some((c) => c.type === ct)) ?? 'website'

  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          {t('contacts.empty')}
        </p>
      )}
      {value.map((c, i) => {
        const err = showErrors ? contactError(c) : ''
        return (
          <div key={i} className="rounded-md border bg-background p-2">
            <div className="grid grid-cols-[minmax(0,170px)_minmax(0,1fr)_auto] items-center gap-2">
              <Select value={c.type} onValueChange={(v) => patch(i, { type: v as ContactType })}>
                <SelectTrigger aria-label={t('contacts.typeAria')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map((ct) => (
                    <SelectItem key={ct} value={ct}>
                      <span className="inline-flex items-center gap-2"><ContactTypeIcon type={ct} />{CONTACT_META[ct].name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={c.label}
                onChange={(e) => patch(i, { label: e.target.value })}
                placeholder={CONTACT_META[c.type].label}
                maxLength={40}
                aria-label={t('contacts.labelAria')}
              />
              <div className="flex gap-0.5">
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0}
                  onClick={() => move(i, -1)} aria-label={t('contacts.moveUp')}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === value.length - 1}
                  onClick={() => move(i, 1)} aria-label={t('contacts.moveDown')}><ArrowDown className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                  onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label={t('contacts.remove')}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
            <Input
              value={c.url}
              onChange={(e) => patch(i, { url: e.target.value })}
              placeholder={CONTACT_META[c.type].placeholder}
              aria-label={c.type === 'phone' ? t('contacts.phoneAria') : c.type === 'email' ? t('common:fields.email') : t('contacts.urlAria')}
              aria-invalid={!!err}
              className={`mt-2 ${err ? 'border-destructive' : ''}`}
            />
            {err && <p className="mt-1 text-[11px] text-destructive">{err}</p>}
          </div>
        )
      })}
      <Button
        type="button" variant="outline" size="sm" disabled={value.length >= MAX_CONTACTS}
        onClick={() => onChange([...value, { type: nextType, label: '', url: '' }])}
      >
        <Plus className="h-4 w-4" /> {t('contacts.add')}
      </Button>
      <p className="text-[11px] text-muted-foreground">
        {t('contacts.hint', { max: MAX_CONTACTS })}
      </p>
    </div>
  )
}
